import { useEffect, useState } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import {
  Avatar,
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialButton from "../../components/MaterialButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { BASE_URL } from "../Utils/Baseurl";
import axios from "axios";
import { useLoading } from "../../context/loadingcontext";
import noData from "../../assest/NoDataImage.png";
import ChatBot from "../chatbot";
import CanAccess from "../../components/permission/CanAccess";
import MaterialBox from "../../components/MaterialBox";

const Project = () => {
  const navigate = useNavigate();
  const { setCirleLoading } = useLoading();
  const [project, setProject] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const open = Boolean(anchorEl);
  const [chatBotStatus, setChatBotStatus] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const currentUser = sessionStorage.getItem("currentUser");

  useEffect(() => {
    fetchProjectData();
    fetchChatList();
  }, []);

  const fetchChatList = async () => {
    setCirleLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.post(
        `${BASE_URL}ai/ManagerProjectAnalysis/`,
        { user_id: currentUser.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setChatBotStatus(true);
      setSuggestion(res?.data?.analysis || res?.data.message);
    } catch (error) {
      setSuggestion("");
      setChatBotStatus(false);
    } finally {
      setCirleLoading(false);
    }
  };

  /**
   * Fetches a summary of all projects from the API and updates the state with the retrieved data.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, sets the project state with the fetched data.
   * Logs any errors encountered during the fetch operation.
   */
  const fetchProjectData = async () => {
    setCirleLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.post(
        `${BASE_URL}api/projects-summary/`,
        { user_id: currentUser },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data.data;
      setProject(data);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setCirleLoading(false);
    }
  };

  /**
   * Deletes a project from the API based on the selectedProjectId.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, logs the response data.
   * Logs any errors encountered during the delete operation.
   */
  const projectDeleteData = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.delete(`${BASE_URL}api/deleteProject/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id: selectedProjectId,
        },
      });
      navigate("/project-list");
      fetchProjectData();
    } catch (error) {
      console.log(error, "error");
    } finally {
      setCirleLoading(false);
    }
  };

  const handleClick = (event, projectId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProjectId(projectId);
  };

  const handleClose = (action) => {
    if (action && selectedProjectId) {
      if (action === "View") {
        navigate(`/project-list/view-project`, {
          state: { projectId: selectedProjectId },
        });
      } else if (action === "Edit") {
        navigate("/project-list/add-project/", {
          state: { projectId: selectedProjectId },
        });
      } else if (action === "Delete") {
        projectDeleteData();
      }
    }
    setAnchorEl(null);
    setSelectedProjectId(null);
  };

  const handleAddProject = () => {
    navigate("/project-list/add-project");
  };

  return (
    <DashboardLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        p={1}
        mb={2}
      >
        <Stack direction="column" spacing={0}>
          <MaterialTypography variant="h5" fontWeight="bold" color="dark">
            Project
          </MaterialTypography>
          <MaterialTypography variant="caption" color="text">
            You can view the projects here.
          </MaterialTypography>
        </Stack>
        <CanAccess module="Project" component="Project Add">
          <MaterialButton
            variant="contained"
            color="dark"
            onClick={handleAddProject}
          >
            Add Project
          </MaterialButton>
        </CanAccess>
      </Stack>
      {chatBotStatus && (
        <div className="chatbox-container">
          <ChatBot
            suggestionText={suggestion}
            style={{ marginTop: "-100px", marginRight: "180px" }}
          />
        </div>
      )}
      {project.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            textAlign: "center",
          }}
        >
          <img
            src={noData}
            alt="No Projects"
            style={{ width: "250px", height: "25    0px" }}
          />
        </Box>
      ) : (
        <Grid container spacing={1} sx={{ mt: 2, p: 2 }}>
          {project.map((item) => (
            <Grid size={{ xs: 12, sm: 4, md: 2.4, lg: 2.4 }} key={item.project_id}>
              <Card sx={{
                borderRadius: '24px',
                overflow: 'visible',
                position: 'relative',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                border: '1px solid #f0f0f0',
              }}>
                <IconButton
                  onClick={(event) => handleClick(event, item.project_id)}
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    width: 30,
                    height: 30,
                    zIndex: 1,
                  }}
                >
                  <MoreVertIcon sx={{ color: '#333', fontSize: '0.7rem' }} />
                </IconButton>
                <Box sx={{ px: 1.5, pb: 1 }}>
                  <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1, mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Avatar
                        src={item.project_logo}
                        sx={{
                          width: 45,
                          height: 45,
                          borderRadius: '50%',
                          border: '2px solid #fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          backgroundColor: '#f0f0f0'
                        }}
                      />
                      <Box>
                        <Tooltip title={item.project_name} disableHoverListener={item.project_name.length <= 15}>
                          <MaterialTypography variant="body2" fontWeight="bold" sx={{ color: '#1a1a1a', fontSize: '0.8rem', mb: 0.4, cursor: 'pointer' }}>
                            {item.project_name.length > 15 ? `${item.project_name.substring(0, 15)}...` : item.project_name}
                          </MaterialTypography>
                        </Tooltip>
                        <MaterialTypography variant="body2" color="text" sx={{ fontSize: '0.8rem', mb: 0.4 }}>
                          {item.project_code}
                        </MaterialTypography>
                      </Box>
                    </Stack>

                    <Box sx={{ backgroundColor: '#f5f8ff', borderRadius: '20px', p: 1.5, mb: 1.5 }}>
                      <MaterialTypography variant="body2" fontWeight="bold" sx={{ color: '#333', mb: 1, fontSize: '0.9rem' }}>
                        Projects Metrics
                      </MaterialTypography>
                      <Stack spacing={1}>
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#666' }}>Progress</MaterialTypography>
                            </Stack>
                            <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                              {item.completion_percent}%
                            </MaterialTypography>
                          </Stack>
                          <Box sx={{
                            width: '100%',
                            height: 6,
                            backgroundColor: '#c0d2fbff',
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            <Box sx={{
                              width: `${item.completion_percent}%`,
                              height: '100%',
                              backgroundColor: '#7ca3fdff',
                              borderRadius: 3,
                              transition: 'width 0.5s ease-in-out'
                            }} />
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#666' }}>Days Left :</MaterialTypography>
                          <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                            {item.days_left}
                          </MaterialTypography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Stack spacing={1} mb={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MaterialTypography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Manager</MaterialTypography>
                        </Stack>
                        <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          {item.manager || "Not Assigned"}
                        </MaterialTypography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MaterialTypography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Start Date</MaterialTypography>
                        </Stack>
                        <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          {item.start_date || "N/A"}
                        </MaterialTypography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MaterialTypography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>End Date</MaterialTypography>
                        </Stack>
                        <MaterialTypography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          {item.end_date || "N/A"}
                        </MaterialTypography>
                      </Box>
                    </Stack>

                    {(() => {
                      const getStatusStyle = (status) => {
                        const s = status?.toLowerCase() || "";
                        if (s.includes("track") || s.includes("on track") || s.includes("Completed"))
                          return { bg: '#e6f9f1', color: '#10b981' };
                        if (s.includes("delayed") || s.includes("Abort"))
                          return { bg: '#fff4e5', color: '#f5590bff' };
                        if (s.includes("completed") || s.includes("finished"))
                          return { bg: '#e0f2fe', color: '#0ea5e9' };
                        if (s.includes("Hold") || s.includes("Hold"))
                          return { bg: '#f3f4f6', color: '#f6cf32ff' };
                        if (s.includes("Not Started"))
                          return { bg: '#fef2f2', color: '#f5946bff' };
                        if (s.includes("In Progress"))
                          return { bg: '#f5f3ff', color: '#8b5cf6' };
                        return { bg: '#f5f8ff', color: '#5243f5' };
                      };
                      const style = getStatusStyle(item.status);
                      return (
                        <MaterialBox
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '30px',
                            width: '100%',
                            height: '30px',
                            backgroundColor: style.bg,
                            color: style.color,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            px: 2,
                            mt: 1
                          }}
                        >
                          {item.status || "N/A"}
                        </MaterialBox>
                      );
                    })()}
                  </Box>
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={open && selectedProjectId === item.project_id}
                  onClose={() => handleClose("")}
                  elevation={3}
                  sx={{
                    '& .MuiPaper-root': {
                      borderRadius: '12px',
                      minWidth: 90,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem onClick={() => handleClose("Edit")} sx={{ py: 1, gap: 1 }}>
                    Edit Project
                  </MenuItem>
                  <MenuItem onClick={() => handleClose("View")} sx={{ py: 1, gap: 1 }}>
                    View Details
                  </MenuItem>
                  <MenuItem onClick={() => handleClose("Delete")} sx={{ py: 1, gap: 1, color: 'error.main', '&:hover': { backgroundColor: '#fff5f5' } }}>
                    Delete Project
                  </MenuItem>
                </Menu>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </DashboardLayout>
  );
};

export default Project;
